import { Component, ElementRef, Input, NgZone, OnDestroy, OnInit, ViewChild, HostListener, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Subscription, interval, filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { MultilangPipe } from '../trans/multilang.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { ProgressKoleckoComponent } from './progress-kolecko';
import { PostText } from "./post-text";
import { WebGalleryCard, WebPhoto,
    GALLERY_INDICATORS, GALLERY_PREVIEW, GALLERY_TITLE, GALLERY_BOXES_2, GALLERY_BOXES_3, GALLERY_SMALL_INDI,
    POST_EXPANDED, AUTO_ASPECT
} from '../models/models';
import { GalleryStateService, LogLine, ImageDownloader, getUrlFilename } from '../services/gallery-state.service';
/* TODO - remove this - only for DEVEL */
import { isDevMode } from '@angular/core';

//////////////////// fs-gallery-card.ts ////////////////////
/*
Angular standalone component: FsGalleryCard
- Works on desktop and mobile
- Fullscreen toggle (click / Escape / touch)
- Slides (buttons on desktop; swipe on mobile)
- Zoom (buttons on desktop; pinch on mobile) + pan while zoomed
- Slideshow that pauses when entering fullscreen

- fs-gallery-card.ts  (component logic, standalone)
- fs-gallery-card.html (template)
- fs-gallery-card.scss (styles)
*/

const TAP_MAX_DIST   = 32;  // px
const TAP_MAX_TIME   = 250; // ms
const FLICK_MIN_DIST = 20;  // px
const FLICK_MAX_TIME = 400; // ms
const ROTATE_ANGLE_ON_KEYPRESS = 3;   // angle [deg] - The image is rotated by this angle on keypress (Ctrl-Arrow)
const ROTATE_GESTURE_ANGLE_THRESHOLD = 20;   // angle [deg] - Initiate Rotate gesture by turning at least by a given angle
const ROTATE_GESTURE_ANGLE_THRESHOLD_RAD = ROTATE_GESTURE_ANGLE_THRESHOLD * Math.PI / 180;
const ROTATE_GESTURE_ENABLE_DELAY    = 2000; // delay [ms]  - Initiate 3-finger Rotate gesture by holding the pointers at least for a given time

const DEBUG_SLIDESHOW = false;
const DEBUG_TRANSFORM = false;
const DEBUG_TRANSITION = false;
const DEBUG_METRICS = false;
const DEBUG_MEDIA_BOX = false;
const DEBUG_POINTERS_ROTATE = true;
const DEBUG_FULLSCREEN_AND_VISIBILITY = false;
const DEBUG_POINTERS = false;
const DEBUG_DOWNLOAD_CACHE = false;

const DEFAULT_SLIDESHOW_TRANSITION = 500; // 500
const DEFAULT_TOUCH_TRANSITION = 200; // 200
const DOWNLOAD_TASK_FADEOUT = 3000;
const LOAD_HIRES_DELAY = 1000;
const LOAD_HIRES_CONCURRENT_DELAY_PER_TASK = 1000;
const GESTURE_CROSSMARK_FADEOUT = 2000;
// How much of the viewport the image must keep covered while panning.
// 1   = the whole viewport (strict, image edges can never enter the view)
// 0   = only the viewport centre (loose, image may be dragged half out of view)
// Values in between trade one for the other.
const CLAMP_VIEWPORT_COVERAGE = 1;
// How often the gesture overlay (kolecko, compass, debug bar) is re-rendered
// while a gesture is running. The transforms themselves are written straight to
// the DOM every frame, only the Angular bindings are refreshed at this rate.
const GESTURE_UI_REFRESH_MS = 100;

// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---

interface GalleryImage {
    thumb: string;
    full?: string;
    type: string;
    zoom: number;
    angle: number;
}

interface Metrics {
    naturalWidth: number;
    naturalHeight: number;
    renderedWidth: number;
    renderedHeight: number;
}

type Coords2D = {
    x: number;
    y: number;
};

type ImageCacheMap = Map<string, HTMLImageElement>;
type ImagePromiseMap = Map<string, Promise<HTMLImageElement>>;

type TouchState = 'idle' | 'touch' | 'swipe' | 'drag' | 'pinch' | 'rotate';
type TransitionState = 'idle' | 'slideToNext' | 'slideToPrev' | 'snapBack';


function numNf(value : number, fracNo : number = 1, padChars: number = 0) {
    return value.toFixed(fracNo).padStart(padChars, ' ');
}
function normalizeAngle(angle: number): number {
    const twoPi = 2 * Math.PI;
    return ((angle + Math.PI) % twoPi + twoPi) % twoPi - Math.PI;
}

// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---

@Component({
    selector: 'fs-gallery-card',
    standalone: true,
    imports: [CommonModule, ButtonModule, ProgressKoleckoComponent, PostText, TranslateModule],
    templateUrl: './fs-gallery-card.html',
    styleUrls: ['./fs-gallery-card.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
})

export class FsGalleryCard implements OnInit, OnDestroy {
    private destroyRef = inject(DestroyRef);

    // Instance Id
    static instanceCount : number = 0;
    private myId = 'G-' + (++FsGalleryCard.instanceCount);
    private logNo : number = 0;

    @Input() set setData(value: WebGalleryCard) {
        this.data = value;

        this.data.items.forEach((item) => {
            if (!item.thumb) {
                item.thumb = item.src.replace(FsGalleryCard.regexpImgUrl, "/assets/thumb/");
            }
            this.images.push({
                full: item.src,
                thumb: item.thumb,
                zoom: item.zoom ? item.zoom : 0,
                angle: item.angle ? (item.angle * Math.PI / 180) : 0,
                type: 'img'
            });
        });
        this.logMsg(`setData: @${this.myId} images=${this.images.length}`);

        this.showTitle       = (value.style! & GALLERY_TITLE     ) != 0;
        this.showThumbnails  = (value.style! & GALLERY_PREVIEW   ) != 0;
    }
    @Input() startIndex = 0;
    @Input() slideshowIntervalMs = 5000; // default slideshow
    @Input() slideshowTransitionMs = DEFAULT_SLIDESHOW_TRANSITION; // ms
    @Input() touchTransitionMs     = DEFAULT_TOUCH_TRANSITION; // ms
    private readonly slideMinOpacity = 0;
    private readonly slideOutOpacity = 0.5;

    @ViewChild('viewport', { static: true }) viewportRef!: ElementRef<HTMLDivElement>;
    @ViewChild('prevSlot')   prevSlotRef?:   ElementRef<HTMLDivElement>;
    @ViewChild('activeSlot') activeSlotRef?: ElementRef<HTMLDivElement>;
    @ViewChild('nextSlot')   nextSlotRef?:   ElementRef<HTMLDivElement>;
    @ViewChild('koleckoSlot', { static: true }) koleckoSlotRef?: ElementRef<HTMLDivElement>;
    
    data: WebGalleryCard = {
        text: "",
        style: null,
        items : []
    }
    images: GalleryImage[] = [];

    // gallery features
    showTitle: boolean = false;
    showThumbnails: boolean = false;

    // TODO: move Regexes somewhere else
    static regexpImgUrl = /^\/assets\/img\//;
    static regexpAssetUrl = /^\/assets\/(thumb|img)\//;
    static regexOnlyDashes = /^-+$/;
    static regexpDeblob = /^blob.*\/\/.*\//;
    //static regexpLogImgUrl = /^.*\/assets\/(img|thumb)\//;

    // onscreen visibility
    private isVisibleOnScreen = false;
    private isGlobalFullscreenActive = false;
    private observer: IntersectionObserver | null = null;
    private subs = new Subscription();
    isFullscreen: boolean = false;
    isPseudoFullscreen: boolean = false;

    // viewport size
    private viewportWidth = 0;
    private viewportHeight = 0;
    // Cached viewport rect. getBoundingClientRect() inside pointermove forces a
    // synchronous layout on every event; combined with the style writes of the
    // previous event that is classic layout thrashing.
    private cachedViewportRect: DOMRect | null = null;
    // True while at least one pointer is down (drives will-change + DOM fast path)
    isInteracting = false;

    // slideshow state
    activeIndex: number = 0;
    wantPlay: boolean = true; // Default: true = Slideshow is playing if not Full-screen
    isPlaying: boolean = false;
    canRun: boolean = false;
    private slideshowSub: Subscription | null = null;
    slotTransition : string = 'none'; // TODO: move template attributes somewhere else

    // view state
    scale = 1;
    minScale = 1;
    readonly maxScale = 27;
    viewOffset: Coords2D = { x:0, y:0 };
  
    // pointer tracking for gestures
    private pointers = new Map<number, PointerEvent>();
    private tapGesture: { x: number; y: number; time: number } = { x: 0, y: 0, time: 0 };
    private swipeGesture: { id: number; x: number; y: number; time: number } | null = null;
    private deltaX = 0;
    private viewAngle = 0;
    private pinchGesture: { 
        startDist: number,
        startScale: number,
        startAngle: number,
        lastAngle: number,
        centerRel: { x: number, y: number }, // Pinch center relative
        isRotating: boolean, // Rotating was already started
        time: number
    } = {
        startDist: 0,
        startScale: 1,
        startAngle: 0,
        lastAngle: 0,
        centerRel: { x: 0, y: 0 }, // Pinch center relative
        isRotating: false,
        time: 0

    };
    private rotateGesture: { 
        startPoints: Coords2D[],
        startAngles: number[],
        startViewAngle: number,
        lastViewAngle: number,
        startDist: number,
        startScale: number,
        centerRel: { x: number, y: number }, // Gesture center relative
        time: number
    } = { 
        startPoints: [],
        startAngles: [],
        startViewAngle: 0,
        lastViewAngle: 0,
        startDist: 1,
        startScale: 1,
        centerRel: { x: 0, y: 0 }, // Gesture center relative
        time: 0
    };

    // ------------- constructor and ng-logic -------------

    constructor(private host: ElementRef<HTMLElement>, private multilangPipe: MultilangPipe, private http: HttpClient, private galleryService: GalleryStateService, private changeDetector : ChangeDetectorRef, private zone: NgZone) {}

    ngOnInit() {
        this.logMsg("ngOnInit");

        this.initSources();
    
        this.updateMediaLabel();

        const el = this.viewportRef.nativeElement;

        // All high frequency input is handled OUTSIDE Angular.
        // A pointermove fires 100-240x/s on a phone and every single one used to
        // trigger a full application change detection pass, which is the main
        // reason the drag/rotate felt like it randomly froze for ~1s.
        this.zone.runOutsideAngular(() => {
            // 1. POINTER EVENTS (none of them calls preventDefault -> passive)
            el.addEventListener('pointerdown', this.onPointerDown, { passive: true });
            el.addEventListener('pointermove', this.onPointerMove, { passive: true });
            el.addEventListener('pointerup', this.onPointerUp, { passive: true });
            el.addEventListener('pointercancel', this.onPointerCancel, { passive: true });

            // 2. TOUCH EVENTS - non-passive on purpose.
            // The wrapper uses `touch-action: pan-y`, so the browser owns the
            // gesture until we call preventDefault(). While it is still deciding
            // whether the user wants to scroll, it throttles and eventually
            // cancels our pointermove stream - which is exactly why a pinch or a
            // rotation feels like it hangs for a while before it starts moving.
            el.addEventListener('touchstart', this.onTouchStart, { passive: false });
            el.addEventListener('touchmove', this.onTouchMove, { passive: false });

            // 3. LAYOUT INVALIDATION
            window.addEventListener('resize', this.onResize, { passive: true });
            window.addEventListener('scroll', this.invalidateViewportRect, { passive: true, capture: true });
        });

        // Debug print throttle. Kept INSIDE the Angular zone on purpose: it is
        // the only thing that ticks change detection while nothing else happens,
        // so log lines written from the out-of-zone gesture handlers actually
        // reach the on screen .debug-logger. Dev builds only.
        if (isDevMode()) {
            this.ptrMoveSub = interval(700).subscribe(() => {
                this.ptrMoveCanPrint = true;
                this.ptrRotateCanPrint = true;
                this.ptrMediaBoxCanPrint = true;
                this.ptrClampCanPrint = true;
                this.ptrTransCanPrint = true;
            });
        }

        this.observer = this.galleryService.createObserver((visible) => {
            this.isVisibleOnScreen = visible;
               if (DEBUG_FULLSCREEN_AND_VISIBILITY && isDevMode()) this.logMsg(`this.observer: @${this.myId} visible=${visible}`);
            this.updateSlideshowEnabled();
        });
        // Start watching this component's element
        this.observer.observe(el);

        this.subs.add(
            this.galleryService.activeFullscreenId$.subscribe(activeId => {
                this.isGlobalFullscreenActive = activeId !== null && activeId !== this.myId;
                if (DEBUG_FULLSCREEN_AND_VISIBILITY && isDevMode()) this.logMsg(`activeFullscreenId$: Local @${this.myId} Fullscreen=${activeId}${this.isGlobalFullscreenActive?' g.full':''}`);
                this.updateSlideshowEnabled();
            })
        );
    }

    ngOnDestroy() {
        // Stop gallery actions
        this.isDestroyed = true;
        this.stopSlideshow();
        this.cancelOngoingTransition();
        this.cancelScheduledTransforms();
        this.destroyDownloadTasks();

        // REMOVE EVENTS

        // Resize / scroll events
        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('scroll', this.invalidateViewportRect, true);

        const el = this.viewportRef.nativeElement;
        // 1. Pointer events
        el.removeEventListener('pointerdown', this.onPointerDown);
        el.removeEventListener('pointermove', this.onPointerMove);
        el.removeEventListener('pointerup', this.onPointerUp);
        el.removeEventListener('pointercancel', this.onPointerCancel);
        // 2. Touch events
        el.removeEventListener('touchstart', this.onTouchStart);
        el.removeEventListener('touchmove', this.onTouchMove);

        // REMOVE SUBSCRIPTIONS

        // Remove Intersect Observer
        this.observer?.disconnect();

        // Remove subscriptions
        this.subs.unsubscribe();
        this.clearSlideshowTimer();
        this.clearTransitionTimer();
        this.ptrMoveSub?.unsubscribe();
    }

    ngAfterViewInit() {
        this.logMsg("ngAfterViewInit");

        // GALLERY SERVICE - CALLBACK FOR DOWNLOAD REMOVAL
        // - Called when a Download is removed by Gallery Service
        // - Gallery informs about removal of all downloads GLOBALLY
        // - We have to filter for our components
        this.galleryService.downloadRemoval$
        .pipe(
            filter(id => this.downloadsMap.has(id)),
            takeUntilDestroyed(this.destroyRef))
        .subscribe(id => this.removeDownloadCallback(id));

        setTimeout(() => {
            this.recalcViewport();
            this.updateView();
        });
    }

    ngDoCheck() {
        //console.count('CD');
    }

    // ---------------- download cache ----------------

    public localDownloads: ImageDownloader[] = [];
    public downloadsMap = new Map<string, ImageDownloader>;

    // ---------------- image cache ----------------

    private thumbCache: ImageCacheMap = new Map();
    private thumbPromiseCache: ImagePromiseMap = new Map();
    private fullCache: ImageCacheMap = new Map();
    private fullPromiseCache: ImagePromiseMap = new Map();

    // ---------------- image logic ----------------

    // src binding
    prevSrc = '';
    activeSrc = '';
    nextSrc = '';
    hiresImageLoaded: boolean = false;
    hiresImageSrc = '';

    // transforms
    prevTransform = '';
    activeTransform = '';
    nextTransform = '';
    slotOpacityPrev : number = this.slideMinOpacity;
    slotOpacityNext : number = this.slideMinOpacity;
    slotOpacityActive : number = 1;

    // metrics
    prevMetrics?: Metrics;
    activeMetrics?: Metrics;
    nextMetrics?: Metrics;
    activeViewBoundingBox: Coords2D = { x:0, y:0 }

    // layout snapshot (freeze)
    layout!: {
        prev: Metrics;
        active: Metrics;
        next: Metrics;
    };

    touchState: TouchState = 'idle';
    transitionState: TransitionState = 'idle';

    // init

    async initSources() {
        this.logMsg(`initSources(): @${this.myId}`);
        this.navigateToIndex();
        await this.loadMetricsForCurrent();
        this.disableTransition();
        this.updateTransforms();
        this.preloadNeighbours(this.activeIndex);
    }

    // ---------------- metrics

    loadMetricsSync(src: string): Metrics | undefined {
        if (!this.thumbCache.has(src))
            return undefined;
        const img = this.thumbCache.get(src);
        if (!img)
            return undefined;
        const rect = this.getViewportRect();
        const scale = Math.min(
            rect.width / img.naturalWidth,
            rect.height / img.naturalHeight
        );
        return {
            naturalWidth: img.naturalWidth,
            naturalHeight: img.naturalHeight,
            renderedWidth: img.naturalWidth * scale,
            renderedHeight: img.naturalHeight * scale
        };
    }

    async loadMetrics(src: string): Promise<Metrics> {
        //const label = `LoadMetrics: ${src.substring(src.lastIndexOf('/')+1)}`;
        //console.time(label);
        const img = await this.preloadThumb(src);
        //console.timeEnd(label);
        if (!img) {
            return {
                naturalWidth: 1,
                naturalHeight: 1,
                renderedWidth: 1,
                renderedHeight: 1
            };
        }

        const rect = this.getViewportRect();

        const scale = Math.min(
            rect.width / img.naturalWidth,
            rect.height / img.naturalHeight
        );

        return {
            naturalWidth: img.naturalWidth,
            naturalHeight: img.naturalHeight,
            renderedWidth: img.naturalWidth * scale,
            renderedHeight: img.naturalHeight * scale
        };
    }

    async loadMetricsForCurrentSync() {
        try {
            const prevSrc = this.images[this.getIndex(- 1)].thumb;
            const actiSrc = this.images[this.activeIndex  ].thumb;
            const nextSrc = this.images[this.getIndex(+ 1)].thumb;
            this.prevMetrics   = this.loadMetricsSync(prevSrc)
            this.activeMetrics = this.loadMetricsSync(actiSrc);
            this.nextMetrics   = this.loadMetricsSync(nextSrc)
            this.recalcActiveMedia();
        } catch (e) {
            this.logMsg(`loadMetricsForCurrentSync: ${e}`);
        }
    }

    async loadMetricsForCurrent() {
        try {
            const oldIndex = this.activeIndex;
            const prevSrc = this.images[this.getIndex(- 1)].thumb;
            const actiSrc = this.images[this.activeIndex  ].thumb;
            const nextSrc = this.images[this.getIndex(+ 1)].thumb;

            const results = await Promise.allSettled([
                this.loadMetrics(actiSrc),
                this.loadMetrics(prevSrc),
                this.loadMetrics(nextSrc)
            ]);

            if(oldIndex != this.activeIndex) {
                this.logMsg(`loadMetricsForCurrent: oldIndex != this.activeIndex ${oldIndex} ${this.activeIndex}`);
                return;
            }

            if (results[0].status === 'fulfilled') {
                this.activeMetrics = results[0].value;
                this.recalcActiveMedia();
            }
            if (results[1].status === 'fulfilled') this.prevMetrics = results[1].value;
            if (results[2].status === 'fulfilled') this.nextMetrics = results[2].value;

            if (DEBUG_METRICS && isDevMode()) this.logMsg(`loadMetrics ASYNC: #${this.activeIndex} [${numNf(this.prevMetrics!.renderedWidth)} | ${numNf(this.activeMetrics!.renderedWidth)} | ${numNf(this.nextMetrics!.renderedWidth)}]`);
            if ((DEBUG_METRICS && DEBUG_TRANSFORM) && isDevMode()) this.logMsg(`loadMetrics------: [${getUrlFilename(prevSrc)}/${getUrlFilename(actiSrc)}/${getUrlFilename(nextSrc)}]`);

            if (results[0].status === 'rejected') this.logMsg(`loadMetrics(${this.activeSrc  }): error: ${results[0].reason}`);
            if (results[1].status === 'rejected') this.logMsg(`loadMetrics(${this.prevMetrics}): error: ${results[1].reason}`);
            if (results[2].status === 'rejected') this.logMsg(`loadMetrics(${this.nextMetrics}): error: ${results[2].reason}`);
        } catch (e) {
            this.logMsg(`loadMetricsForCurrent: ${e}`);
        }
    }

    recalcActiveMedia() {
        if (!this.activeMetrics) return;
        const media = this.activeMetrics;

        // Rotating media box
        const rx = media.renderedWidth;
        const ry = media.renderedHeight;
        const sin = Math.sin(this.viewAngle);
        const cos = Math.cos(this.viewAngle);
        const x1 = Math.abs(rx * cos);
        const y1 = Math.abs(ry * sin);
        const x2 = Math.abs(rx * sin);
        const y2 = Math.abs(ry * cos);
        this.activeViewBoundingBox.x = x1 + y1;
        this.activeViewBoundingBox.y = x2 + y2;

        if (DEBUG_MEDIA_BOX && isDevMode() && this.ptrMediaBoxCanPrint) {
            this.ptrMediaBoxCanPrint = false;
            this.logMsg(`MEDIA: [${numNf(media.renderedWidth)},${numNf(media.renderedHeight)}] BOX:[${numNf(this.activeViewBoundingBox.x)},${numNf(this.activeViewBoundingBox.y)}]`);
        }
    }

    calcMinScaleForMetrics(metrics: Metrics, active: boolean = false) {
        return 1;
        /*
        const scaleX = this.viewportWidth / metrics.naturalWidth;
        const scaleY = this.viewportHeight / metrics.naturalHeight;
        const minScale = Math.max(1, Math.min(scaleX, scaleY, this.maxScale));
        return active ? 1 : minScale;
        */
    }

    private koleckoPosition: Coords2D = { x:0, y:0 };
    private koleckoTimerId: number | null = null;
    
    public koleckoVisible: boolean = false;
    public koleckoVisibleSolid: boolean = false;
    public slotKoleckoCoords: string = '';
    public slotKoleckoTransition: string = 'none';
    public rotationCompassVisible: boolean = false;
    public slotRotationCompass: string = '';

    private updateRotationCompassAndKolecko() {
        this.rotationCompassVisible = this.isRotationCompassVisible();
        this.slotRotationCompass = `rotate(${this.viewAngle}rad)`;
        this.updateKoleckoVisible();
        this.updateKoleckoTransition();
        this.updateKoleckoCoords();
        if (isDevMode()) {
            this.dbgGesture = `[${numNf(this.viewOffset.x,1,5)},${numNf(this.viewOffset.y,1,5)}]\nα = ${numNf(this.viewAngle * 180 / Math.PI,1,4)}°`
        }
    }

    public updateKoleckoVisible() {
        // if(!isDevMode()) return false;
        const wantKolecko = this.touchState == 'rotate' || (this.touchState == 'pinch' && this.pinchGesture.isRotating);
        if (this.koleckoVisibleSolid != wantKolecko) {
            this.koleckoVisibleSolid = wantKolecko;
            if (wantKolecko) {
                this.koleckoVisible = true;
                this.updateKoleckoTransition();
            } else {
                if (this.koleckoTimerId !== null)
                    clearTimeout(this.koleckoTimerId);
                // updateKoleckoVisible() is reached from the gesture rAF, i.e.
                // outside the Angular zone - the timer has to come back in or
                // the fade out would never be rendered.
                this.koleckoTimerId = setTimeout(() => this.zone.run(() => {
                    const wantAgain = this.touchState == 'rotate' || (this.touchState == 'pinch' && this.pinchGesture.isRotating);
                    if (this.koleckoVisible != wantAgain) {
                        this.koleckoVisible = wantAgain;
                        this.updateKoleckoTransition();
                    }
                }), GESTURE_CROSSMARK_FADEOUT);
            }
        }
    }

    private updateKoleckoCoords() {
        this.slotKoleckoCoords = `translate(${this.koleckoPosition.x}px, ${this.koleckoPosition.y}px)`;
    }

    public updateKoleckoTransition() {
        // Opacity only - the position is written straight to the DOM every frame
        // and must follow the fingers 1:1, so it must not be tweened.
        // Not tied to gestureRotateActive - that flag is cleared on the last
        // finger up, which is exactly when the fade out has to run.
        this.slotKoleckoTransition = this.koleckoVisible ? `opacity ${GESTURE_CROSSMARK_FADEOUT}ms ease-out` : 'none';
    }

    public gestureRotateActive: boolean = false;
    public gestureWantRotateState: boolean = false;
    private gestureWantRotateTimerId: number | null = null;

    private gestureRotateClearTimer() {
        if (this.gestureWantRotateTimerId !== null) {
            clearTimeout(this.gestureWantRotateTimerId);
            this.gestureWantRotateTimerId = null;
        }
    }
    private gestureRotateActivate() {
        this.logMsg(`Rotate: ENABLED`);
        this.gestureRotateClearTimer();
        this.gestureRotateActive = true;
        this.updateKoleckoTransition();
    }
    private gestureCanRotate() : boolean {        
        const wantRotate = this.touchState == 'rotate' && this.pointers.size === 3;
        if (this.gestureWantRotateState != wantRotate) {
            this.gestureWantRotateState = wantRotate;
            if (DEBUG_POINTERS_ROTATE && isDevMode()) {
                this.logMsg(`Rotate: WANT=${wantRotate}`);
            }
            if (wantRotate) {
                this.gestureRotateClearTimer();
                this.gestureWantRotateTimerId = setTimeout(() => this.zone.run(() => {
                    const activate = this.touchState == 'rotate' && this.pointers.size === 3;
                    if (activate && !this.gestureRotateActive) {
                        this.gestureRotateActivate();
                    }
                }), ROTATE_GESTURE_ENABLE_DELAY);
            }
        }
        return this.gestureRotateActive;
    }

    private isRotationCompassVisible() {
        if (this.viewAngle != 0)
            return true;
        if (!this.gestureRotateActive)
            return false;
        if (this.touchState == 'rotate')
            return true;
        if (this.touchState == 'pinch' && this.pinchGesture.isRotating)
            return true;
        return false;
    }

    clickRotationCompass() {
        if (this.touchState == 'rotate')
            return;
        this.viewAngle = 0;
        this.recalcActiveMedia();
        this.clampActiveMedia();
        this.updateTransforms();
    }

    private swipeDistanceThreshold() {
        return this.getViewportWidth() * 0.25;
    }


    // ---------------- transforms

    updateTransforms() {
        let activeW : number = 0;
        let prevW = -1;
        let nextW = -1;
        let prevX : number = 0;
        let nextX : number = 0;        
        if (this.activeMetrics) {
            activeW = this.activeMetrics.renderedWidth;
        }
        if (this.prevMetrics) {
            // let minScalePrev: number = this.calcMinScaleForMetrics(this.prevMetrics);
            prevW = this.prevMetrics.renderedWidth; // / minScalePrev;
            prevX = (activeW / 2 + prevW / 2);
        }
        if (this.nextMetrics) {
            // let minScaleNext: number = this.calcMinScaleForMetrics(this.nextMetrics);
            nextW = this.nextMetrics.renderedWidth; // / minScaleNext;
            nextX = (activeW / 2 + nextW / 2);
        }

        const swipeDist = this.swipeDistanceThreshold();
        const swipeFillRatio = Math.min(1, Math.abs(this.deltaX) / swipeDist);
        let opacityRatioPrev = this.slideMinOpacity;
        let opacityRatioCntr = 1;
        let opacityRatioNext = this.slideMinOpacity;
        if (this.deltaX < 0) {
            // Swipe left -> Next slide
            opacityRatioCntr = 1;
            opacityRatioNext = this.slideMinOpacity + swipeFillRatio * (this.slideOutOpacity - this.slideMinOpacity);
        } else if (this.deltaX > 0) {
            // Swipe right -> Prev slide
            opacityRatioCntr = 1;
            opacityRatioPrev = this.slideMinOpacity + swipeFillRatio * (this.slideOutOpacity - this.slideMinOpacity);
        }

        // Future translation
        const leftX = this.deltaX - prevX;
        let   cntrX = this.deltaX + 0;
        const rghtX = this.deltaX + nextX;
        const prevAngle = this.images[this.getIndex(- 1)].angle;
        const nextAngle = this.images[this.getIndex(+ 1)].angle;
        // Add current translation
        cntrX      += this.viewOffset.x;
        const cntrY = this.viewOffset.y;

        // if (isDevMode() && this.ptrTransCanPrint) {
        //     this.ptrTransCanPrint = false;
        //     this.logMsg(`Transform : prevW=${numNf(prevW)} activeW=${numNf(activeW)} nextW='${numNf(nextW)}' -> [${numNf(leftX)} ${numNf(cntrX)}x${numNf(cntrY)} ${numNf(rghtX)}]`);
        //     this.logMsg(`opacity---: ${numNf(opacityRatioPrev)} < ${numNf(opacityRatioCntr)} > ${numNf(opacityRatioNext)} dx=${numNf(this.deltaX)}`);
        // }
        if (DEBUG_TRANSFORM && isDevMode() && this.ptrTransCanPrint) {
            this.ptrTransCanPrint = false;
            this.logMsg(`TRANSFORM : prevW=${numNf(prevW)} activeW=${numNf(activeW)} nextW='${numNf(nextW)}' -> [${numNf(leftX)} ${numNf(cntrX)}x${numNf(cntrY)} ${numNf(rghtX)}]`);
        }

        this.prevTransform   = `translate3d(${leftX}px, 0px, 0) rotate(${prevAngle}rad)`;
        //this.activeTransform = `translate3d(${cntrX}px, ${cntrY}px, 0) scale(${this.scale})`;
        this.activeTransform = `translate3d(${cntrX}px, ${cntrY}px, 0) rotate(${this.viewAngle}rad) scale(${this.scale})`;
        this.nextTransform   = `translate3d(${rghtX}px, 0px, 0) rotate(${nextAngle}rad)`;
        this.slotOpacityPrev   = opacityRatioPrev;
        this.slotOpacityActive = opacityRatioCntr;
        this.slotOpacityNext   = opacityRatioNext;
        this.updateRotationCompassAndKolecko();
        if (this.isInteracting) {
            // Gesture in progress: we are outside the Angular zone, so bypass CD
            // for the transforms and refresh the rest of the UI at a low rate.
            this.applySlotStylesToDom();
            this.refreshGestureUi();
        } else {
            this.changeDetector.markForCheck();
        }
    }

    // Running outside the Angular zone means markForCheck() has no effect - it
    // marks the view dirty but nothing ever schedules a tick. detectChanges()
    // only checks this component's own view, which is cheap enough to run a few
    // times per second so the gesture overlay stays live.
    private lastGestureRenderTime = 0;
    private isDestroyed = false;
    private refreshGestureUi(force: boolean = false) {
        if (this.isDestroyed) return;
        const now = performance.now();
        if (!force && now - this.lastGestureRenderTime < GESTURE_UI_REFRESH_MS) return;
        this.lastGestureRenderTime = now;
        this.changeDetector.detectChanges();
    }

    // ---------------- animations

    calcPanelMetrics() {
        // Calc side images min scale
        let minScalePrev: number = this.layout.prev ? this.calcMinScaleForMetrics(this.layout.prev) : 1;
        let minScaleNext: number = this.layout.next ? this.calcMinScaleForMetrics(this.layout.next) : 1;
        const activeW = this.layout.active ? this.layout.active.renderedWidth : 1;
        const prevW = this.layout.prev ? (this.layout.prev.renderedWidth / minScalePrev) : 0;
        const nextW = this.layout.next ? (this.layout.next.renderedWidth / minScaleNext) : 0;

        const prevX = (activeW / 2 + prevW / 2);
        const nextX = (activeW / 2 + nextW / 2);
        return [ prevW, activeW, nextW, prevX, nextX, minScalePrev, minScaleNext ];
    }

    slideToNext() {
        const slideInProgress = this.isSlideActive();
        if (slideInProgress) {
            if (DEBUG_TRANSFORM && isDevMode()) this.logMsg(`slide-Next: SLIDE IN PROGRESS: ${this.transitionState}`);
            this.commitTransition(false);

        }

        this.transitionState = 'slideToNext';

        this.freezeLayout();

        // Calc side images min scale
        const [ prevW, activeW, nextW, prevX, nextX, minScalePrev, minScaleNext ] = this.calcPanelMetrics();

        // Future translation
        const leftX = -nextX - prevX;
        const cntrX = -nextX;
        const rghtX = 0;
        const prevAngle = this.images[this.getIndex(- 1)].angle;
        const nextAngle = this.images[this.getIndex(+ 1)].angle;

        const isSwipe = this.touchState === 'swipe';
        if (DEBUG_TRANSFORM && isDevMode()) this.logMsg(`slide-Next: prevW=${numNf(prevW)} activeW=${numNf(activeW)} nextW='${numNf(nextW)}' -> [${numNf(leftX)} ${numNf(cntrX)} ${numNf(rghtX)}] swipe=${isSwipe?'1':'0'}`);
        if (!DEBUG_TRANSFORM && isDevMode()) this.logMsg(`SLIDE-Next: ${this.activeIndex} >>>`);
        const shortSlide = slideInProgress || !isSwipe;
        
        if (shortSlide) {
            if (DEBUG_TRANSFORM && isDevMode()) this.logMsg(`slide-Next: SHORT`);
            this.disableTransition();
            if (slideInProgress)
                this.slotOpacityActive = 0;
            this.nextTransform   = `translateX(${0.1*nextX}px) rotate(${nextAngle}rad) scale(1)`;
        }

        this.startAnimationFrame(() => {
            this.enableTransition();
            this.prevTransform   = `translateX(${leftX}px) scale(${minScalePrev}) rotate(${prevAngle}rad)`;
            const cntr2 = shortSlide ? 0.1 * cntrX : cntrX;
            this.activeTransform = `translateX(${cntr2}px) rotate(${this.viewAngle}rad)`;
            this.nextTransform   = `translateX(${rghtX}px) scale(${minScaleNext}) rotate(${nextAngle}rad)`;
            this.slotOpacityPrev = 0;
            this.slotOpacityActive = this.slideMinOpacity;
            this.slotOpacityNext = 1;
            this.changeDetector.markForCheck();
            if (DEBUG_TRANSFORM && isDevMode()) this.logMsg(`TRANS-REAL: [${numNf(leftX)} ${numNf(cntr2)} ${numNf(rghtX)}] >>> >>> >>>`);
        });
    }

    slideToPrev() {
        const slideInProgress = this.isSlideActive();
        if (slideInProgress) {
            if (DEBUG_TRANSFORM && isDevMode()) this.logMsg(`slide-Prev: SLIDE IN PROGRESS: ${this.transitionState}`);
            this.commitTransition(false);
        }

        this.transitionState = 'slideToPrev';
       
        // freeze layout
        this.freezeLayout();

        // Calc side images min scale
        const [ prevW, activeW, nextW, prevX, nextX, minScalePrev, minScaleNext ] = this.calcPanelMetrics();

        // Future translation
        const leftX = 0;
        const cntrX = prevX;
        const rghtX = prevX + nextX;
        const prevAngle = this.images[this.getIndex(- 1)].angle;
        const nextAngle = this.images[this.getIndex(+ 1)].angle;

        const isSwipe = this.touchState === 'swipe';
        if (DEBUG_TRANSFORM && isDevMode()) this.logMsg(`slide-Prev: prevW=${numNf(prevW)} activeW=${numNf(activeW)} nextW='${numNf(nextW)}' -> [${numNf(leftX)} ${numNf(cntrX)} ${numNf(rghtX)}] swipe=${isSwipe?'1':'0'}`);
        if (!DEBUG_TRANSFORM && isDevMode()) this.logMsg(`SLIDE-Prev: <<< ${this.activeIndex}`);
        const shortSlide = slideInProgress || !isSwipe;
        if (shortSlide) {
            if (DEBUG_TRANSFORM && isDevMode()) this.logMsg(`slide-Prev: SHORT`);
            this.disableTransition();
            if (slideInProgress)
                this.slotOpacityActive = 0;
            this.prevTransform   = `translateX(${-0.1*prevX}px) rotate(${prevAngle}rad) scale(1)`;
        }
        this.startAnimationFrame(() => {
            this.enableTransition();
            this.prevTransform   = `translateX(${leftX}px) scale(${minScalePrev}) rotate(${prevAngle}rad)`;
            const cntr2 = shortSlide ? 0.1 * cntrX : cntrX;
            this.activeTransform = `translateX(${cntr2}px) rotate(${this.viewAngle}rad)`;
            this.nextTransform   = `translateX(${rghtX}px) scale(${minScaleNext}) rotate(${nextAngle}rad)`;
            if (DEBUG_TRANSFORM && isDevMode()) this.logMsg(`TRANS-REAL: [${numNf(leftX)} ${numNf(cntr2)} ${numNf(rghtX)}] <<< <<< <<<`);
            this.slotOpacityPrev = 1;
            this.slotOpacityActive = this.slideMinOpacity;
            this.slotOpacityNext = 0;
            this.changeDetector.markForCheck();
        });
    }

    snapBack() {
        const slideInProgress = this.isSlideActive();
        if (slideInProgress) {
            if (DEBUG_TRANSFORM && isDevMode()) this.logMsg(`Snap-Back: SLIDE IN PROGRESS: ${this.transitionState}`);
            this.commitTransition(true);
        }

        this.transitionState = 'snapBack';

        this.deltaX = 0;
        this.enableTransition();
        this.updateTransforms();
    }

    isSlideActive() {
        if (this.transitionState === 'slideToNext'
            || this.transitionState === 'slideToPrev'
            || this.transitionState === 'snapBack'
        ) {
            return true;
        }
        return false;
    }

    freezeLayout() {
        // freeze layout
        // const activeW = this.activeMetrics!.renderedWidth;
        // const prevW = this.prevMetrics!.renderedWidth;
        // const nextW = this.nextMetrics!.renderedWidth;
        // this.logMsg(`freeze  ---: prevW=${numNf(prevW)} activeW=${numNf(activeW)} nextW='${numNf(nextW)}'`);
        this.layout = {
            prev: this.prevMetrics!,
            active: this.activeMetrics!,
            next: this.nextMetrics!
        };
    }

    // ---------------- transition

    getIndex(offset: number): number {
        const len = this.data.items.length;
        const len2 = this.images;
        return (this.activeIndex + offset + len) % len;
    }

    transitionTimer : any;
    clearTransitionTimer() {
        if (this.transitionTimer) {
            clearTimeout(this.transitionTimer);
            this.transitionTimer = undefined;
        }
    }
    setupTransitionTimer(delayMs : number) {
        if (DEBUG_TRANSITION) this.logMsg(`transitionTimer: @${this.myId} START`);
        this.clearTransitionTimer();
        this.transitionTimer = setTimeout(() => {
            if (DEBUG_TRANSITION) this.logMsg(`transitionTimer: @${this.myId} TIMEOUT`);
            this.onTransitionEnd();
        }, delayMs + 100);
    }

    private animationFrameId: number | null = null;
    startAnimationFrame(transitionFn: () => any) {
        this.cancelOngoingTransition();
        this.animationFrameId = requestAnimationFrame(() => {
            this.animationFrameId = requestAnimationFrame(transitionFn);
        });
    }
    cancelOngoingTransition() {
        if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
    }

    doTransition(index: number, async: boolean) {
        // this.logMsg(`doTransition: @${this.myId} idx->${index}`);

        this.clearTransitionTimer();

        this.navigateToIndex(index);
        if (async)
            this.updateView();
        else
            this.updateViewSync();

        this.preloadNeighbours(this.activeIndex);

        if (this.isPlaying) {
            this.resetSlideshowTimer();
        }

        // this.logMsg(`doTransition: end`);
    }

    commitTransition(async: boolean = true) {
        if (DEBUG_TRANSITION && isDevMode()) this.logMsg(`commitTransition: @${this.myId}`);
        if (this.transitionState === 'slideToNext') {
            this.transitionState = 'idle';
            this.doTransition(this.getIndex(1), async);
        } else if (this.transitionState === 'slideToPrev') {
            this.transitionState = 'idle';
            this.doTransition(this.getIndex(-1), async);
        } else if (this.transitionState === 'snapBack') {
            this.transitionState = 'idle';
        }
        this.flushPendingSrcUpdate();
        this.changeDetector.markForCheck();
    }

    async onTransitionEnd(ev?: TransitionEvent) {
        //if (this.slotTransition == 'none')
        //    return;
        if (DEBUG_TRANSITION && isDevMode()) this.logMsg(`onTransitionEnd: @${this.myId} ${this.transitionState} ${this.slotTransition}`);
        this.commitTransition();
    }

    // ---------------- downloads / preload / cache

    private addDownload(downloader: ImageDownloader) {
        if (DEBUG_DOWNLOAD_CACHE && isDevMode()) this.logMsg(`Download cache: @${this.myId} ADD DOWNLOAD url=${getUrlFilename(downloader.url)}`);
        this.localDownloads.unshift(downloader);
        const downloadId = this.galleryService.addDownload(downloader);
        downloader.downloadId = downloadId;
        this.downloadsMap.set(downloadId, downloader);
    }

    removeDownloadCallback = (id: string) => {
        // Called when a Download is removed by Gallery Service.
        // (The downloads that are not for us, are filtered out in downloadRemoval$ subscription.)
        const downloader = this.downloadsMap.get(id);
        this.downloadsMap.delete(id);
        if (downloader) {
            if (this.fullCache.has(downloader.url)) {
                if (DEBUG_DOWNLOAD_CACHE && isDevMode()) this.logMsg(`Download cache: @${this.myId} REMOVE IMG url=${getUrlFilename(downloader.url)}`);
                this.fullCache.delete(downloader.url);
            }
            if (this.fullPromiseCache.has(downloader.url)) {
                if (DEBUG_DOWNLOAD_CACHE && isDevMode()) this.logMsg(`Download cache: @${this.myId} REMOVE PROMISE url=${getUrlFilename(downloader.url)}`);
                this.fullPromiseCache.delete(downloader.url);
            }
            downloader.isError = true;
            downloader.cancel();
        }
    }

    destroyDownloadTasks() {
        for (let downloader of this.localDownloads) {
            downloader.cancel();
            this.galleryService.removeDownload(downloader.downloadId);            
        }
    }

    preload(src: string | undefined, imageCache: ImageCacheMap, promiseCache: ImagePromiseMap, useBlob: boolean): Promise<HTMLImageElement> {
        if (src === undefined)
            return Promise.reject("src=undefined");
        if (imageCache.has(src)) {
            return Promise.resolve(imageCache.get(src)!);
        }
        if (promiseCache.has(src)) {
            if (DEBUG_DOWNLOAD_CACHE && isDevMode()) this.logMsg(`preload: @${this.myId} return promise src='${getUrlFilename(src)}'`);
            return promiseCache.get(src)!;
        }
        const match = src.match(FsGalleryCard.regexpAssetUrl);
        const srcType = (match && match.length>1) ? match[1].toUpperCase() : '';
        if (DEBUG_DOWNLOAD_CACHE && isDevMode()) this.logMsg(`preload: @${this.myId} [${srcType}]='${getUrlFilename(src)}`);

        const downloader = new ImageDownloader(this.http, src, useBlob);
        downloader.setLogMsg((prefix, msg) => this.galleryService.logMsg(prefix, msg));

        if(useBlob) {
            this.addDownload(downloader);
        }

        const promise = downloader.fetch();
        promiseCache.set(src, promise);

        promise.then((img: HTMLImageElement) => {
            imageCache.set(src, img);
            promiseCache.delete(src);
            this.updateActiveImgSource();
            if (DEBUG_DOWNLOAD_CACHE && isDevMode()) this.logMsg(`Download: COMPLETE ${getUrlFilename(src)} [${img.naturalWidth}x${img.naturalHeight}]`);
        })
        .catch((error: any) => {
            promiseCache.delete(src);
            this.logMsg(`Download: ERROR\nurl='${src}' error='${error}'`);
        });
        if (useBlob) {
            promise.finally(() => {
                const timeout = downloader.isError ? 2*DOWNLOAD_TASK_FADEOUT : 0;
                setTimeout(() => {
                    if (DEBUG_DOWNLOAD_CACHE && isDevMode()) this.logMsg(`Download #${downloader.downloadId} FADEOUT`);
                    this.galleryService.removeDownload(downloader.downloadId);
                    downloader.isActive = false;
                    // Remove visualization element after a given interval
                    setTimeout(() => {
                        downloader.isVisible = false;
                        const count1 = this.localDownloads.length;
                        this.localDownloads = this.localDownloads.filter(i => i !== downloader);
                        const count2 = this.localDownloads.length;
                        if (DEBUG_DOWNLOAD_CACHE && isDevMode()) this.logMsg(`Download #${downloader.downloadId} VIS REMOVE: ${count1}->${count2}`);
                    }, DOWNLOAD_TASK_FADEOUT);
                }, timeout);
            });
        }
        return promise;
    }
    
    preloadThumb(src: string | undefined): Promise<HTMLImageElement> {
        return this.preload(src, this.thumbCache, this.thumbPromiseCache, false);
    }
    preloadHiRes(src: string | undefined): Promise<HTMLImageElement> {
        return this.preload(src, this.fullCache, this.fullPromiseCache, true);
    }

    preloadNeighbours(index: number, count = 2) {
        let images = this.data.items;
        for (let i = 1; i <= count; i++) {
            const next = images[(index + i) % images.length];
            const prev = images[(index - i + images.length) % images.length];

            this.preloadThumb(next.thumb);
            this.preloadThumb(prev.thumb);
        }
    }

    async loadHiRes(newIndex: number) {
        const wantHiRes = this.isFullscreen || this.scale >= 1.1;
        if (!wantHiRes)
            return;
        if (!this.galleryService.canStartDownload())
            return;

        if (newIndex != this.activeIndex) {
            return;
        }

        const galleryImage = this.images[this.activeIndex];
        if (!galleryImage?.full) return;

        if (this.fullCache.has(galleryImage.full)) {
            return; // Hi-Res image download in progress
        }

        try {
            const imagePromise = this.preloadHiRes(galleryImage.full);

            const image = await imagePromise;

            if (newIndex == this.activeIndex) {
                this.hiresImageSrc = image.src;
                this.hiresImageLoaded = true;
                this.updateActiveImgSource();
            }
            const printUrl = getUrlFilename(image.src.replace(FsGalleryCard.regexpDeblob,'BLOB:'));
            this.logMsg(`loadHiRes: @${this.myId} LOADED ${printUrl}`);
        } catch (error) {           
            this.logMsg(`loadHiRes: ${galleryImage.full} ERROR=${error}`)
        }
    }

    loadHiResDelayed(newIndex: number) {
        const wantHiRes = this.isFullscreen || this.scale >= 1.1;
        if (!wantHiRes)
            return;

        const galleryImage = this.images[this.activeIndex];
        if (!galleryImage?.full) return;

        if (this.fullCache.has(galleryImage.full)) {
            const image = this.fullCache.get(galleryImage.full)!;
            this.hiresImageSrc = image.src;
            this.hiresImageLoaded = true;
            const printUrl = getUrlFilename(image.src.replace(FsGalleryCard.regexpDeblob,'BLOB:'));
            this.logMsg(`loadHiRes: @${this.myId} CACHED ${getUrlFilename(printUrl)}`);
            return;
        }

        if (this.fullPromiseCache.has(galleryImage.full)) {
            return; // Hi-Res image download in progress
        }

        const downloadCount = this.galleryService.getActiveDownloadsNumber();
        const downloadDelay = LOAD_HIRES_DELAY + Math.max(0, downloadCount - 1) * LOAD_HIRES_CONCURRENT_DELAY_PER_TASK;
        setTimeout(() => {
            this.loadHiRes(newIndex);
        }, downloadDelay);
    }

    private pendingActiveSrcUpdate = false;

    updateActiveImgSource() {
        const thumbSrc = this.images[this.activeIndex].thumb;
        const newSrc = this.hiresImageLoaded ? this.hiresImageSrc : thumbSrc;
        if (this.activeSrc == newSrc) {
            this.pendingActiveSrcUpdate = false;
            return;
        }
        // Pointing an <img> at a full resolution bitmap makes the browser decode
        // and rasterize it. On a phone that is easily several hundred ms of main
        // thread work - exactly the random freeze felt while dragging/rotating.
        // Postpone it until the gesture/animation is over.
        if (this.isInteracting || this.transitionState !== 'idle') {
            this.pendingActiveSrcUpdate = true;
            return;
        }
        this.pendingActiveSrcUpdate = false;
        this.activeSrc = newSrc;
        this.logMsg(`ACTIVE IMAGE: @${this.myId} #${this.activeIndex} --> ${getUrlFilename(thumbSrc)}${this.hiresImageLoaded?' (Hi-Res)':''}`);
    }

    private flushPendingSrcUpdate() {
        if (this.pendingActiveSrcUpdate) {
            this.updateActiveImgSource();
        }
    }

    updateSources() {
        const prevIndex = this.getIndex(- 1);
        const nextIndex = this.getIndex(+ 1);

        // this.logMsg(`updateSources: @${this.myId} prevSrc[${prevIndex}]=${this.prevSrc}=${this.images[prevIndex]?.thumb}`);
        // this.logMsg(`updateSources: @${this.myId} nextSrc[${nextIndex}]=${this.nextSrc}=${this.images[nextIndex]?.thumb}`);
        this.updateActiveImgSource();
        this.prevSrc = this.images[prevIndex].thumb;
        this.nextSrc = this.images[nextIndex].thumb;
    }

    // ------------- fullscreen control -------------

    async toggleFullscreen() {
        const el = this.viewportRef?.nativeElement;
        
        if (!el) return;

        const isFullscreenActive = this.isSelfFullscreen();

        if (!isFullscreenActive) {
            if (el.requestFullscreen) {
                try {
                    await el.requestFullscreen();
                } catch (error) {
                    this.logMsg(`await el.requestFullscreen(); error=${error}`);
                }
            } else if ((el as any).webkitRequestFullscreen) {
                try {
                    await (el as any).webkitRequestFullscreen();
                } catch (error) {
                    this.logMsg(`await (el as any).webkitRequestFullscreen();\nerror=${error}`);
                }
            } else {
                // iPhone fallback: Turn pseudo-fullscreen only if API doesn't exist
                this.isPseudoFullscreen = true;
                this.onFullscreenChange();
            }
        } else {
            // Turn off fullscreen
            if (document.exitFullscreen) {
                try {
                    await document.exitFullscreen();
                } catch (error) {
                    this.logMsg(`await document.exitFullscreen();\nerror=${error}`);
                }
            } else if ((document as any).webkitExitFullscreen) {
                try {
                    await (document as any).webkitExitFullscreen();
                } catch (error) {
                    this.logMsg(`await (document as any).webkitExitFullscreen();\nerror=${error}`);
                }
            }
            this.isPseudoFullscreen = false;
            this.onFullscreenChange();
        }
    }

    private isSelfFullscreen(): boolean {
        const el = this.viewportRef?.nativeElement;
        if (!el) return false;
        return (document.fullscreenElement == el) || (document as any).webkitFullscreenElement == el || this.isPseudoFullscreen;
    }

    @HostListener('document:fullscreenchange')
    private onFullscreenChange = () => {
        const newIsFullscreen = this.isSelfFullscreen();
        this.cachedViewportRect = null;
        if (this.isFullscreen != newIsFullscreen) {
            this.isFullscreen = newIsFullscreen;
            if (isDevMode()) this.logMsg(`Fullscreen: @${this.myId} ${this.isFullscreen}`);
            this.galleryService.setFullscreen(this.isFullscreen ? this.myId : null);
        }

        // stop slideshow when fullscreen, resume when exiting only if it was playing
        if (this.isFullscreen) {
            this.stopSlideshow();
            this.loadHiResDelayed(this.activeIndex);
        } else {
            this.resetTransform();
            this.updateSlideshowEnabled();
        }
    };

    private fitImageToViewport() {
        /*
        const el = this.viewportRef.nativeElement.querySelector('img, video');
        if (!el) return;
        let imgWidth = 1;
        let imgHeight = 1;
        if (el instanceof HTMLImageElement) {
            imgWidth = el.naturalWidth;
            imgHeight = el.naturalHeight;
        } else if (el instanceof HTMLVideoElement) {
            imgWidth = el.videoWidth;
            imgHeight = el.videoHeight;
        }
        if (!imgWidth || !imgHeight) return;
        */

        if (!this.activeMetrics) return;
        // this.minScale = Math.max(this.calcMinScaleForMetrics(this.activeMetrics, true), this.images[this.activeIndex].zoom);
        this.minScale = this.calcMinScaleForMetrics(this.activeMetrics, true);
        this.scale = this.minScale;

        this.clampActiveMedia();
    }

    private onResize = () => {
        this.zone.run(() => {
            if (isDevMode()) this.logMsg(`onResize: @${this.myId}`);
            this.recalcViewport();
            this.updateView();
        });
    };

    isZoomed() {
        return this.scale > 1.2 * this.minScale;
    }

    isNonScrollable() {
        return this.isFullscreen || this.isZoomed();
    }

    // ------------- slideshow -------------

    setupSlideshowTimer() {
        if (DEBUG_SLIDESHOW && isDevMode()) this.logMsg(`setupSlideshowTimer()`);
        if (this.slideshowSub) {
            this.slideshowSub.unsubscribe();
            this.slideshowSub = null;
        }
        this.slideshowSub = interval(this.slideshowIntervalMs)
        .subscribe(() => {
            if (DEBUG_SLIDESHOW) this.logMsg(`Slideshow: TIMER ${this.touchState}/${this.transitionState}`);
            //console.count("Slideshow");
            //this.logMsg(`Slideshow: ${this.transitionState}`);
            if (this.transitionState === 'idle') {
                this.slideToNext();
                this.changeDetector.markForCheck();
            } else {
                //this.logMsg(`Slideshow: ${this.touchState}/${this.transitionState}`);
            }
        });
    }
    clearSlideshowTimer() {
        this.slideshowSub?.unsubscribe();
        this.slideshowSub = null;
    }

    startSlideshow() {
        if (this.slideshowSub) return;
        if (isDevMode()) this.logMsg(`Slideshow: START @${this.myId}`);
        this.isPlaying = true;
        this.setupSlideshowTimer();
    }

    stopSlideshow() {
        if (DEBUG_SLIDESHOW && isDevMode()) this.logMsg(`Slideshow: STOP @${this.myId}`);
        this.isPlaying = false;
        this.clearSlideshowTimer();
    }

    toggleSlideshow() {
        this.isPlaying = !this.isPlaying;
        this.wantPlay = this.isPlaying;

        if (this.isPlaying)
            this.startSlideshow();
        else
            this.stopSlideshow();
    }

    private updateSlideshowEnabled() {
        // Logic: Can run only if visible AND no other gallery is blocking the view via fullscreen
        this.canRun = this.isVisibleOnScreen && !this.isGlobalFullscreenActive;

        if (DEBUG_SLIDESHOW && isDevMode()) this.logMsg(`Slideshow update: want=${this.wantPlay} canRun=${this.canRun} visible=${this.isVisibleOnScreen} g.full=${this.isGlobalFullscreenActive}`);

        if (this.wantPlay && this.canRun) {
            this.startSlideshow();
        } else {
            this.stopSlideshow();
        }
    }

    // ------------- navigation -------------

    navigateToIndex(newIndex: number | undefined = undefined) {
        if (this.activeIndex == newIndex)
            return;
        if (!newIndex) {
            newIndex = this.startIndex || 0;
        } else {
            // Save view params
            this.images[this.activeIndex].angle = this.viewAngle;
        }

        if (isDevMode()) this.logMsg(`NAVIGATE: ${this.activeIndex} -> ${newIndex}`);

        this.activeIndex = newIndex;
        this.hiresImageLoaded = false;
        this.hiresImageSrc = '';

        this.updateMediaLabel();

        this.loadHiResDelayed(newIndex);
        this.updateSources();

        this.resetTransform();
        // Restore view params
        this.scale = Math.max(this.scale, this.images[this.activeIndex].zoom);
        this.viewAngle = this.images[this.activeIndex].angle;
    }
    
    private resetSlideshowTimer() {
        this.setupSlideshowTimer();
    }

    mediaLabel: { title: string, desc: string } | null = null;
    getMediaTitle(): { title: string, desc: string } {
        let title = this.multilangPipe.transform(this.data.items[this.activeIndex].title);
        let desc = this.multilangPipe.transform(this.data.items[this.activeIndex].description);
        if (FsGalleryCard.regexOnlyDashes.test(title))
            title = "";
        if (FsGalleryCard.regexOnlyDashes.test(desc))
            desc = "";
        if (title.length > 0 && desc.length > 0)
            return { title: title, desc: " – " + desc};
        if (title.length > 0)
            return { title: title, desc: ""};
        return { title: "", desc: desc};
    }
    updateMediaLabel() {
        this.mediaLabel = this.getMediaTitle();
    }

    // ------------- zoom / pan -------------

    resetTransform() {
        this.scale = this.minScale;
        this.viewOffset.x = 0;
        this.viewOffset.y = 0;
        // this.viewAngle = 0;
        this.recalcActiveMedia();
        this.deltaX = 0;        
    }

    zoomByPoint(zoomIn: boolean, point?: Coords2D) {
        const viewCenter = {
            x: this.viewportWidth  / 2,
            y: this.viewportHeight / 2
        };

        const center = point ? point : viewCenter;

        // zoom by center of viewport → media relative coords
        const centerRel = this.getMediaRelativeCoords(center);

        if (zoomIn) {
            this.setScale(Math.min(this.scale * 1.25, this.maxScale));
        } else {
            this.setScale(Math.max(this.scale / 1.25, this.minScale));
            /*
            if (this.scale <= this.minScale) {
                // if scale is 1, reset offsets
                this.resetTransform();
            }
            */
        }

        // new media position calculation
        const newRelCoords = this.getMediaAbsoluteCoords(centerRel);
        this.viewOffset.x = center.x - this.viewportWidth  / 2 - newRelCoords.x;
        this.viewOffset.y = center.y - this.viewportHeight / 2 - newRelCoords.y;

        this.recalcActiveMedia();
        this.clampActiveMedia();
        this.updateTransforms();
        
        this.enableTransition(true);
    }
    
    zoomIn() {
        this.zoomByPoint(true);
    }
    zoomOut() {
        this.zoomByPoint(false);
    }

    private setScale(newScale: number) {
        // bound and keep center-ish
        this.scale = Math.max(this.minScale, Math.min(newScale, this.maxScale));
        // Clamp based on viewport/image size
        this.clampActiveMedia();
    }

    private getViewportWidth(): number {
        return this.viewportRef?.nativeElement?.clientWidth || window.innerWidth;
    }
    private getDistance(a: PointerEvent, b: PointerEvent) {
        return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    }
    private getCenter(a: PointerEvent, b: PointerEvent) {
        return {
            x: (a.clientX + b.clientX) / 2,
            y: (a.clientY + b.clientY) / 2
        }
    }
    private getCenter3p(a: PointerEvent, b: PointerEvent, c: PointerEvent) {
        return {
            x: (a.clientX + b.clientX + c.clientX) / 3,
            y: (a.clientY + b.clientY + c.clientY) / 3
        }
    }

    private recalcViewport() {
        const viewport = this.viewportRef.nativeElement;
        this.viewportWidth = viewport.clientWidth;
        this.viewportHeight = viewport.clientHeight;
        this.cachedViewportRect = null;
        if (isDevMode()) this.logMsg(`recalcViewport: @${this.myId} [${numNf(this.viewportWidth)}x${numNf(this.viewportHeight)}]`);
    }

    // Cached bounding rect: reading it during a gesture forces a synchronous
    // layout (reflow) on every pointermove. It is invalidated on resize, scroll,
    // fullscreen change and at the start of every gesture.
    private getViewportRect(): DOMRect {
        if (!this.cachedViewportRect) {
            this.cachedViewportRect = this.viewportRef.nativeElement.getBoundingClientRect();
        }
        return this.cachedViewportRect;
    }
    private invalidateViewportRect = () => {
        this.cachedViewportRect = null;
    };

    // ---------------- frame scheduling

    private rafTransformId: number | null = null;

    // Pointer events arrive faster than the display refreshes. Applying the
    // transform on every event means several style recalcs + composites per
    // frame; the browser then has to catch up and the gesture visibly stutters.
    // Collapse everything into a single write per animation frame instead.
    private scheduleUpdateTransforms() {
        if (this.rafTransformId !== null) return;
        this.rafTransformId = requestAnimationFrame(() => {
            this.rafTransformId = null;
            this.updateTransforms();
        });
    }
    private cancelScheduledTransforms() {
        if (this.rafTransformId !== null) {
            cancelAnimationFrame(this.rafTransformId);
            this.rafTransformId = null;
        }
    }

    // While a gesture is running we are outside the Angular zone, so the style
    // bindings would not be flushed. Write them straight to the DOM - the bound
    // fields stay in sync, so the next regular CD pass is a no-op.
    private applySlotStylesToDom() {
        const prevEl   = this.prevSlotRef?.nativeElement;
        const activeEl = this.activeSlotRef?.nativeElement;
        const nextEl   = this.nextSlotRef?.nativeElement;
        if (prevEl) {
            prevEl.style.transition = this.slotTransition;
            prevEl.style.transform  = this.prevTransform;
            prevEl.style.opacity    = `${this.slotOpacityPrev}`;
        }
        if (activeEl) {
            activeEl.style.transition = this.slotTransition;
            activeEl.style.transform  = this.activeTransform;
            activeEl.style.opacity    = `${this.slotOpacityActive}`;
        }
        if (nextEl) {
            nextEl.style.transition = this.slotTransition;
            nextEl.style.transform  = this.nextTransform;
            nextEl.style.opacity    = `${this.slotOpacityNext}`;
        }
        // The position indicator has to follow the gesture center every frame -
        // at the throttled refreshGestureUi() rate it would move in visible steps.
        const koleckoEl = this.koleckoSlotRef?.nativeElement;
        if (koleckoEl) {
            koleckoEl.style.transform = this.slotKoleckoCoords;
        }
    }

    private rotateMedia(rotAngle: number) {
        const center = {
            x: this.viewportWidth  / 2,
            y: this.viewportHeight / 2
        };
        const centerRel = this.getMediaRelativeCoords(center);

        const deltaAngle = rotAngle * Math.PI / 180;
        this.viewAngle += deltaAngle;

        const newRelCoords = this.getMediaAbsoluteCoords(centerRel);
        this.viewOffset.x = center.x - this.viewportWidth  / 2 - newRelCoords.x;
        this.viewOffset.y = center.y - this.viewportHeight / 2 - newRelCoords.y;

        this.recalcActiveMedia();
        this.clampActiveMedia();
        this.updateTransforms();
        
        this.enableTransition(true);
    }

    private moveMedia(px: number, py: number) {
        if (!this.activeMetrics) return;
        const square = Math.sqrt(this.activeMetrics.renderedWidth * this.activeMetrics.renderedHeight);
        this.viewOffset.x += square * px;
        this.viewOffset.y += square * py;
        this.clampActiveMedia();
        this.updateTransforms();
    }

    private calcClampTranslate(translate: Coords2D, scale: number, metrics: Metrics) {
        const sin = Math.sin(this.viewAngle);
        const cos = Math.cos(this.viewAngle);

        // Everything is solved in the MEDIA frame (the image rotated back to
        // axis aligned). There the image is a plain rectangle and the viewport
        // is the tilted one, so a single min/max per axis covers all 4 image
        // corners AND all 4 edge midpoints exactly - no sampling needed.
        const imgW = metrics.renderedWidth  * scale;
        const imgH = metrics.renderedHeight * scale;

        // Bounding box of the rotated viewport, expressed in media coords.
        const boxW = Math.abs(this.viewportWidth * cos) + Math.abs(this.viewportHeight * sin);
        const boxH = Math.abs(this.viewportWidth * sin) + Math.abs(this.viewportHeight * cos);

        // Pan offset rotated into media coords.
        let mediaX =  translate.x * cos + translate.y * sin;
        let mediaY = -translate.x * sin + translate.y * cos;

        // The image cannot cover the viewport on this axis -> keep it centered,
        // otherwise allow panning until the required coverage is reached.
        const limitX = imgW <= boxW ? 0 : (imgW - boxW * CLAMP_VIEWPORT_COVERAGE) / 2;
        const limitY = imgH <= boxH ? 0 : (imgH - boxH * CLAMP_VIEWPORT_COVERAGE) / 2;

        mediaX = Math.max(-limitX, Math.min(+limitX, mediaX));
        mediaY = Math.max(-limitY, Math.min(+limitY, mediaY));

        // ...and back to view coords.
        translate.x = mediaX * cos - mediaY * sin;
        translate.y = mediaY * cos + mediaX * sin;

        if (DEBUG_MEDIA_BOX && isDevMode() && this.ptrClampCanPrint) {
            this.ptrClampCanPrint = false;
            this.logMsg(`Clamp: scale=${numNf(scale, 2)} img=[${numNf(imgW)}x${numNf(imgH)}] box=[${numNf(boxW)}x${numNf(boxH)}] lim=[${numNf(limitX)},${numNf(limitY)}] T=[${numNf(translate.x)}x${numNf(translate.y)}]`);
        }
    }
    private clampActiveMedia() {
        if (!this.activeMetrics) return;

        this.calcClampTranslate(this.viewOffset, this.scale, this.activeMetrics);

        // Stop Playing if image is zoomed
        if (this.scale > this.minScale) {
            if (this.isPlaying) {
              this.stopSlideshow();
            }
        }
    }

    updateViewSync() {
        if (DEBUG_TRANSFORM && isDevMode()) this.logMsg(`updateViewSync: @${this.myId}`);
        this.loadMetricsForCurrentSync();
        // fit image to viewport if smaller than viewport
        this.fitImageToViewport();
        //this.recalcActiveMedia();
        this.deltaX = 0;
        this.disableTransition();
        this.updateTransforms();
    }

    async updateView() {
        if (DEBUG_TRANSFORM && isDevMode()) this.logMsg(`updateView: @${this.myId}`);
        await this.loadMetricsForCurrent();
        // fit image to viewport if smaller than viewport
        this.fitImageToViewport();
        //this.recalcActiveMedia();
        this.deltaX = 0;
        this.disableTransition();
        this.updateTransforms();
    }

    // ----------- pointer event handlers for gestures -----------
    // pointerdown / pointerup are low frequency and change template state,
    // so they re-enter the Angular zone. pointermove stays outside of it.
    onPointerDown = (ev: PointerEvent) => {
        this.zone.run(() => this.handlePointerDown(ev));
    }

    private handlePointerDown(ev: PointerEvent) {
        try {
            (ev.target as Element).setPointerCapture(ev.pointerId);
        } catch (e) {}

        this.pointers.set(ev.pointerId, ev);
        this.isInteracting = true;
        // Fresh layout read once per gesture instead of once per move event.
        this.cachedViewportRect = null;

        // Fullscreen detection - short tap
        this.tapGesture.x = ev.clientX;
        this.tapGesture.y = ev.clientY;
        const now = performance.now();
        this.tapGesture.time = now;

        if (DEBUG_POINTERS && isDevMode()) this.logMsg(`Pointer Down: id:${ev.pointerId} type:${ev.pointerType} [${this.pointers.size}]`);

        if (this.pointers.size === 1) {   
            this.touchState = 'touch';
            if (DEBUG_POINTERS && isDevMode()) this.logMsg(`Touch: ${numNf(ev.clientX)} x ${numNf(ev.clientY)}`);

            // Disable slideshow if there are any pointers
            this.clearSlideshowTimer();

            this.swipeGesture = { id: ev.pointerId, x: ev.clientX, y: ev.clientY, time: now };
            this.deltaX = 0;
        }

        if (this.pointers.size === 2) {
            this.touchState = 'pinch';
            this.startPinchGesture();
        }

        if (this.pointers.size === 3) {
            this.touchState = 'rotate';
            this.gestureCanRotate();
            this.startRotateGesture();
        }
    }

    startPinchGesture = () => {
        const [p1, p2] = Array.from(this.pointers.values());

        this.pinchGesture.startDist = this.getDistance(p1, p2);
        this.pinchGesture.startScale = this.scale;
        this.pinchGesture.isRotating = false; // Default 2 fingers 'pinch' does not rotate

        this.pinchGesture.startAngle = Math.atan2(p1.clientY - p2.clientY, p1.clientX - p2.clientX);
        this.pinchGesture.lastAngle = this.pinchGesture.startAngle;

        const centerGlobal = this.getCenter(p1, p2);
        const rect = this.getViewportRect();
        const center = {
            x: centerGlobal.x - rect.left,
            y: centerGlobal.y - rect.top
        };

        // viewport coord system → media relative coords
        this.pinchGesture.centerRel = this.getMediaRelativeCoords(center);

        if ((DEBUG_POINTERS || DEBUG_POINTERS_ROTATE) && isDevMode()) {
            this.logMsg(`Pinch: ${numNf(center.x)} x ${numNf(center.y)} α=${numNf(this.viewAngle * 180/Math.PI)}°`);
        }

        // No CSS transition during a live gesture: every new transform would
        // restart a 200ms tween from the interpolated value, so the image lags
        // behind the fingers and never catches up while the gesture continues.
        this.disableTransition();
    }

    startRotateGesture = () => {
        const [p1, p2, p3] = Array.from(this.pointers.values());

        // calculate 3 points center
        const centerGlobal = this.getCenter3p(p1, p2, p3);
        const rect = this.getViewportRect();
        const center = {
            x: centerGlobal.x - rect.left,
            y: centerGlobal.y - rect.top
        };

        this.rotateGesture.centerRel = this.getMediaRelativeCoords(center);

        this.rotateGesture.startPoints = []; 
        let angles: number[] = []; 
        let sumDist = 0;
        for (let pt of this.pointers.values()) {
            const coords = { x: pt.clientX, y: pt.clientY }
            this.rotateGesture.startPoints.push(coords);
            const angle = Math.atan2(coords.y - centerGlobal.y, coords.x - centerGlobal.x);
            sumDist += Math.hypot(coords.y - centerGlobal.y, coords.x - centerGlobal.x);
            angles.push(angle);
        }
        this.rotateGesture.startAngles = angles;
        
        this.rotateGesture.startViewAngle = this.viewAngle;
        this.rotateGesture.lastViewAngle = this.viewAngle;
        this.rotateGesture.startDist = sumDist;
        this.rotateGesture.startScale = this.scale;
        
        this.koleckoPosition = center;

        if (DEBUG_POINTERS_ROTATE && isDevMode()) {
            this.logMsg(`Rotate: ${numNf(center.x)} x ${numNf(center.y)} α[3]=[${numNf(angles[0] * 180/Math.PI)},${numNf(angles[1] * 180/Math.PI)},${numNf(angles[2] * 180/Math.PI)}]`);
        }

        // A live gesture must follow the fingers 1:1 - no tween in between.
        this.disableTransition();
    }

    ptrCount : number = 0;
    private ptrMoveCanPrint : boolean = true;
    private ptrRotateCanPrint : boolean = true;
    private ptrMediaBoxCanPrint : boolean = true;
    private ptrClampCanPrint : boolean = true;
    private ptrTransCanPrint : boolean = true;
    // Created in ngOnInit (dev mode only, outside the Angular zone) - as a field
    // initializer it used to wake up change detection every 700ms in production.
    private ptrMoveSub: Subscription | null = null;

    onPointerMove = (ev: PointerEvent) => {
        if (!this.pointers.has(ev.pointerId)) {
            return;
        }
        this.pointers.set(ev.pointerId, ev);

        const now = performance.now(); // TODO: move

        // --- single pointer: pan / swipe ---
        if (this.pointers.size === 1 && this.swipeGesture && this.swipeGesture.id === ev.pointerId) {
            const dx = ev.clientX - this.swipeGesture.x;
            const dy = ev.clientY - this.swipeGesture.y;
            if (this.touchState == 'touch') {
                const isZoomed = this.scale > this.minScale;
                const isHorizontal = Math.abs(dx) > 1.1 * Math.abs(dy);
                if (isZoomed) {
                    // pan while zooming
                    this.touchState = 'drag';
                    this.viewOffset.x += dx;
                    this.viewOffset.y += dy;
                    this.clampActiveMedia();
                    this.swipeGesture.x = ev.clientX;
                    this.swipeGesture.y = ev.clientY;
                    this.disableTransition();
                    this.scheduleUpdateTransforms();
                    this.ptrMoveCanPrint = true;
                }
                else if (isHorizontal) {
                    // swipe preview
                    this.touchState = 'swipe';
                    this.disableTransition();
                    this.ptrMoveCanPrint = true;
                }
                else {
                    this.swipeGesture.x = ev.clientX;
                    this.swipeGesture.y = ev.clientY;
                }
            } else if (this.touchState == 'swipe') {
                this.deltaX = ev.clientX - this.swipeGesture.x;
                this.scheduleUpdateTransforms();
            } else if (this.touchState == 'drag') {
                this.viewOffset.x += ev.clientX - this.swipeGesture.x;
                this.viewOffset.y += ev.clientY - this.swipeGesture.y;
                this.clampActiveMedia();
                this.scheduleUpdateTransforms();
                this.swipeGesture.x = ev.clientX;
                this.swipeGesture.y = ev.clientY;
            }
            if (DEBUG_POINTERS && isDevMode() && (this.ptrCount != this.pointers.size || this.ptrMoveCanPrint)) {
                this.ptrMoveCanPrint = false;
                this.ptrCount = this.pointers.size;
                this.logMsg(`${this.capitalizeFirst(this.touchState)} ${numNf(dx)} x ${numNf(dy)}`);
            }
        }

        // --- two pointers: pinch ---
        if (this.pointers.size === 3 && (this.touchState == 'pinch')) {
            /// TODO move to onPointerDown? on third finger start rotation instantly
            this.touchState = 'rotate';
            this.startRotateGesture();
        }

        if ((this.pointers.size === 2 || this.pointers.size === 3) && (this.touchState == 'pinch')) {
            const [p1, p2] = Array.from(this.pointers.values());

            const dist = this.getDistance(p1, p2);
            const scaleFactor = dist / this.pinchGesture.startDist;

            const centerGlobal = this.getCenter(p1, p2);

            if (isDevMode() && (this.ptrCount != this.pointers.size || this.ptrMoveCanPrint)) {
                this.ptrMoveCanPrint = false;
                this.ptrCount = this.pointers.size;
                this.logMsg(`Pinch: ${numNf(centerGlobal.x)} x ${numNf(centerGlobal.y)}`);
            }

            const rect = this.getViewportRect();
            const centerLocal = {
                x: centerGlobal.x - rect.left,
                y: centerGlobal.y - rect.top
            };
            const center = {
                x: centerLocal.x - this.viewportWidth / 2,
                y: centerLocal.y - this.viewportHeight / 2
            };

            const newAngle = Math.atan2(p1.clientY - p2.clientY, p1.clientX - p2.clientX);
            // Angle turned since the two fingers touched down. Radians here -
            // the threshold constant is declared in degrees.
            const totalAngle = normalizeAngle(newAngle - this.pinchGesture.startAngle);
            if (this.pointers.size === 2 && !this.pinchGesture.isRotating) {
                if (Math.abs(totalAngle) >= ROTATE_GESTURE_ANGLE_THRESHOLD_RAD) {
                    this.gestureRotateActivate();
                    this.pinchGesture.isRotating = true;
                    // Continue from the current finger angle. Without this the
                    // first rotating frame would apply the whole threshold at
                    // once and the image would visibly snap by 20 degrees.
                    this.pinchGesture.lastAngle = newAngle;
                }
            }
            if (this.pinchGesture.isRotating) {
                let deltaAngle = normalizeAngle(newAngle - this.pinchGesture.lastAngle);
                this.pinchGesture.lastAngle = newAngle;
                this.viewAngle += deltaAngle;
                this.koleckoPosition = centerLocal;
            }

            this.setScale(this.pinchGesture.startScale * scaleFactor);

            // new media position calculation
            const newRelCoords = this.getMediaAbsoluteCoords(this.pinchGesture.centerRel);
            this.viewOffset.x = center.x - newRelCoords.x;
            this.viewOffset.y = center.y - newRelCoords.y;
            this.recalcActiveMedia();
            this.clampActiveMedia();

            this.scheduleUpdateTransforms();

            if (DEBUG_POINTERS_ROTATE && isDevMode() && this.ptrRotateCanPrint) {
                this.ptrRotateCanPrint = false;
                this.logMsg(`Rotate: αTot=${numNf(totalAngle * 180 / Math.PI)}° ${numNf(center.x)} x ${numNf(center.y)} α=${numNf(this.viewAngle * 180 / Math.PI)}°`);
            }
        }

        // --- three pointers: rotate ---
        if (this.pointers.size === 3 && this.touchState == 'rotate') {
            const pts = Array.from(this.pointers.values());

            // calculate 3 points center
            const centerGlobal = this.getCenter3p(pts[0], pts[1], pts[2]);

            const rect = this.getViewportRect();
            const centerLocal = {
                x: centerGlobal.x - rect.left,
                y: centerGlobal.y - rect.top
            };
            const center = {
                x: centerLocal.x - this.viewportWidth / 2,
                y: centerLocal.y - this.viewportHeight / 2
            };

            let angleSum = 0;
            let angles = [];
            let sumDist = 0;
            for (let i = 0; i < 3; i++) {
                const coords = { x: pts[i].clientX, y: pts[i].clientY }
                const newAngle = Math.atan2(coords.y - centerGlobal.y, coords.x - centerGlobal.x);
                const dist = Math.hypot(coords.y - centerGlobal.y, coords.x - centerGlobal.x);
                sumDist += dist;
                let diff = normalizeAngle(newAngle - this.rotateGesture.startAngles[i]);
                angleSum += diff * dist;
                angles.push(newAngle);
            }
            this.rotateGesture.startAngles = angles;

            let deltaAngle = angleSum / sumDist;

            this.rotateGesture.lastViewAngle += deltaAngle;
            const totalDeltaAngle = (this.rotateGesture.lastViewAngle - this.rotateGesture.startViewAngle) * 180 / Math.PI;
            
            if (!this.gestureRotateActive) {
                if (Math.abs(totalDeltaAngle) >= ROTATE_GESTURE_ANGLE_THRESHOLD) {
                    this.gestureRotateActivate();
                }
            }
            if (this.gestureRotateActive) {
                this.viewAngle += deltaAngle;
                this.recalcActiveMedia();
            }

            this.setScale(this.rotateGesture.startScale * sumDist / this.rotateGesture.startDist);

            // new media position calculation
            const newRelCoords = this.getMediaAbsoluteCoords(this.rotateGesture.centerRel);
            this.viewOffset.x = center.x - newRelCoords.x;
            this.viewOffset.y = center.y - newRelCoords.y;
            this.recalcActiveMedia();
            this.clampActiveMedia();
            
            this.koleckoPosition = centerLocal;

            this.scheduleUpdateTransforms();

            if (DEBUG_POINTERS_ROTATE && isDevMode() && this.gestureRotateActive && this.ptrRotateCanPrint) {
                this.ptrRotateCanPrint = false;
                this.logMsg(`Rotate: ${numNf(center.x)} x ${numNf(center.y)} dα=${numNf(totalDeltaAngle)}° α=${numNf(this.viewAngle * 180 / Math.PI)}° α[3]=[${numNf(angles[0] * 180/Math.PI)},${numNf(angles[1] * 180/Math.PI)},${numNf(angles[2] * 180/Math.PI)}]°`);
            }
        }
    }

    getMediaRelativeCoords(coords: Coords2D) {
        // convert coords: viewport → coords relative to media center
        const viewRelX = (coords.x - this.viewportWidth  / 2 - this.viewOffset.x) / this.scale;
        const viewRelY = (coords.y - this.viewportHeight / 2 - this.viewOffset.y) / this.scale;
        const sin = Math.sin(this.viewAngle);
        const cos = Math.cos(this.viewAngle);
        // relative coords → rotated media coords
        return {
            x: viewRelX * cos + viewRelY * sin,
            y: viewRelY * cos - viewRelX * sin
        };
    }

    getMediaAbsoluteCoords(coords: Coords2D) {
        // rotated media coords → relative coords
        const viewRelX = coords.x * this.scale;
        const viewRelY = coords.y * this.scale;
        const sin = Math.sin(this.viewAngle);
        const cos = Math.cos(this.viewAngle);
        return {
            x: viewRelX * cos - viewRelY * sin,
            y: viewRelY * cos + viewRelX * sin
        };
    }

    onPointerUp = (ev: PointerEvent) => {
        try { (ev.target as Element).releasePointerCapture(ev.pointerId); } catch(e) {}
        this.zone.run(() => this.processPointerUp(ev));
    }

    processPointerUp = (ev: PointerEvent) => {
        this.pointers.delete(ev.pointerId);
        this.isInteracting = this.pointers.size > 0;
        // A frame scheduled by the gesture would apply stale styles behind
        // Angular's back after the gesture already ended.
        this.cancelScheduledTransforms();

        if (this.pointers.size === 0) {
            if (DEBUG_POINTERS && isDevMode()) this.logMsg(`${this.capitalizeFirst(this.touchState)} End: id=${ev.pointerId} type:${ev.pointerType}`);
            if (this.touchState == 'touch' || this.touchState == 'swipe') {
                // Fullscreen detection - short tap
                const dt = performance.now() - this.tapGesture.time;
                const dist = Math.hypot(ev.clientX - this.tapGesture.x, ev.clientY - this.tapGesture.y);
                if (dist <= TAP_MAX_DIST && dt < TAP_MAX_TIME && this.scale <= this.minScale) {
                    const target = ev.target as HTMLElement;
                    if (!target.closest('.controls.desktop')) {
                        this.toggleFullscreen();
                    }
                }
            }
            if (this.touchState == 'swipe' && this.swipeGesture && this.swipeGesture.id === ev.pointerId) {
                // --- swipe logic ---
                if (this.swipeGesture && this.scale <= this.minScale) {
                    const dx = ev.clientX - this.swipeGesture.x;
                    const dt = performance.now() - this.swipeGesture.time;
                    const flick = Math.abs(dx) >= FLICK_MIN_DIST && dt <= FLICK_MAX_TIME;
                    const passedDistance = Math.abs(dx) > this.swipeDistanceThreshold();

                    if (flick || passedDistance) {
                        if (dx < 0)
                            this.slideToNext();
                        else
                            this.slideToPrev();
                    } else {
                        this.snapBack();
                    }
                }

                this.swipeGesture = this.pointers.size === 0 ? null : this.swipeGesture;
            }
            this.touchState = 'idle';
            // Resume slideshow
            if (this.isPlaying) {
                this.resetSlideshowTimer();
            }
            // A high resolution image that arrived mid gesture is swapped in now.
            this.flushPendingSrcUpdate();
        }
        if (this.pointers.size <= 1) {
            // Rotate mode survives finger count changes (3 -> 2) once the
            // threshold was passed, but ends as soon as a single finger is left.
            this.gestureRotateClearTimer();
            this.gestureRotateActive = false;
            this.gestureWantRotateState = false;
            this.pinchGesture.isRotating = false;
        }
        if (this.pointers.size === 1 && this.touchState == 'pinch') {
            // pointers is keyed by pointerId, not by index - take the survivor.
            const pt = this.pointers.values().next().value;
            if (pt) {
                this.touchState = 'touch';
                this.swipeGesture = { id: pt.pointerId, x: pt.clientX, y: pt.clientY, time: performance.now() };
            }
        }
        if (this.pointers.size === 2) {
            this.gestureRotateClearTimer();

            this.touchState = 'pinch';
            this.startPinchGesture();
            if (this.gestureRotateActive) {
                this.pinchGesture.isRotating = true; // We are returning from 3 fingers 'rotation'
            }
        }
        this.updateRotationCompassAndKolecko();
    }

    onPointerCancel = (ev: PointerEvent) => {
        this.zone.run(() => this.processPointerUp(ev));
    }

    onTouchStart = (ev: TouchEvent) => {
        // A multi finger gesture (zoom / rotate) is ours - claim it immediately
        // instead of letting the browser arbitrate it against page scrolling.
        if (ev.touches.length > 1 && ev.cancelable) {
            ev.preventDefault();
        }
    };

    onTouchMove = (ev: TouchEvent) => {
        if (!ev.cancelable) return;
        // One finger on a non zoomed image still scrolls the page (touch-action:
        // pan-y). Everything else is handled by this component.
        if (ev.touches.length > 1 || this.isNonScrollable()) {
            ev.preventDefault();
        }
    };

    onMouseWheel(ev: WheelEvent) {
        // Ignore Wheel, if in fullscreen or image is zoomed
        if (!this.isNonScrollable()) {
            return;
        }

        // Prevent default behavior
        ev.preventDefault();

        const rect = this.getViewportRect();
        // get client coords
        const wheelPointClient = {
            x: ev.clientX - rect.left,
            y: ev.clientY - rect.top
        };
        // deltaY > 0 wheel down (zoom out), deltaY < 0 wheel up (zoom in)
        this.zoomByPoint(ev.deltaY < 0, wheelPointClient);
    }

    // ----------- keyboard control -----------

    @HostListener('window:keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent) {
        if (!this.isFullscreen) return;

        if (event.ctrlKey && event.key != 'Control') {
            switch (event.key) {
                case 'ArrowLeft' : this.rotateMedia(-ROTATE_ANGLE_ON_KEYPRESS); break;
                case 'ArrowRight': this.rotateMedia(+ROTATE_ANGLE_ON_KEYPRESS); break;
                case 'NumpadAdd':
                case '+':          this.zoomIn();  event.preventDefault();      break;
                case 'NumpadSubtract':
                case '-':          this.zoomOut(); event.preventDefault();      break;
            }
        } else if (this.isZoomed()) {
            switch (event.key) {
                case 'ArrowLeft' : this.moveMedia( 0.1,    0); break;
                case 'ArrowRight': this.moveMedia(-0.1,    0); break;
                case 'ArrowUp'   : this.moveMedia(   0,  0.1); break;
                case 'ArrowDown' : this.moveMedia(   0, -0.1); break;
                case 'PageUp'    : this.slideToPrev();         break;
                case 'PageDown':   this.slideToNext();         break;
                case '+':          this.zoomIn();              break;
                case '-':          this.zoomOut();             break;
            }
        } else {
            switch (event.key) {
                case 'PageUp':
                case 'ArrowLeft':
                case 'ArrowUp':
                    this.slideToPrev();
                    break;
                case 'PageDown':
                case 'ArrowRight':
                case 'ArrowDown':
                    this.slideToNext();
                    break;
                case '+':          this.zoomIn();              break;
                case '-':          this.zoomOut();             break;
                case 'Escape':
                    document.exitFullscreen();
                    break;
          }
        }
    }

    // ----------- other events -----------

    onThumbnailButtonClick() {
    }

    private enableTransition(touchTransition: boolean = false) {
        if (this.touchState == 'drag') {
            if (DEBUG_TRANSITION) this.logMsg("enableTransition drag");
        }
        if (DEBUG_TRANSITION) this.logMsg(`enableTransition(${touchTransition})`);
        //const delayMs = this.slideshowTransitionMs * (this.touchState === 'swipe' ? 1 : 0.2);
        const delayMs = touchTransition ? this.touchTransitionMs : this.slideshowTransitionMs;
        this.slotTransition = `transform ${delayMs}ms ease-out, opacity ${delayMs}ms ease-in-out`;
        this.setupTransitionTimer(delayMs);
        //this.slotTransition = 'none';
    }
    private disableTransition() {
        if (DEBUG_TRANSITION) this.logMsg("disableTransition");
        this.slotTransition = 'none';
    }

    // ----------- debug log -----------

    /* TODO - remove this - only for DEVEL */
    isDevMode: boolean = isDevMode();
    showDevLog: boolean = false;
    dbgGesture: string = '';
    getLogLines() {
        return this.galleryService.getDebugLogs().slice(0,123);
    }
    logLineColor(line: LogLine) {
        switch(line.prefix) {
            case 'GSVC': return "#000080c0";
            case 'HTTP': return "#008000c0";
            case 'IMG': return "#008080c0";
            default: return '#000000c0';
        }
    }
    toggleDevLog() {
        this.showDevLog = !this.showDevLog;
    }
    clearDevLog() {
        this.galleryService.clearDevLog();
    }
    private logMsg(msg: string) {
        this.galleryService.logMsg('GAL', msg);
        this.changeDetector.markForCheck();
    }
    capitalizeFirst(str: string): string {
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

}

